/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import jwtDecode from 'jwt-decode';
import api from '@/api';
import { removeObjectKeys } from '@/utils/helpers';

let currentAuthInfoRequest = null;

const user = {
  namespaced: true,

  state() {
    return {
      isLoading: false,
      isLoggedIn: false,
      expireToken: 0,
      roles: [],
      serverTime: '',
      status: {},
      categories: [],
      workTypes: [],
      userData: {},
      dynamicServerTime: '', // на клиенте каждые 5 сек пересчитываем
    };
  },

  mutations: {
    setLoggedIn(state, value) {
      state.isLoggedIn = value;
    },
    setExpireToken(state, value) {
      state.expireToken = value;
    },
    changeLoading(state, value) {
      state.isLoading = value;
    },
    setServerTime(state, time) {
      state.serverTime = time;
      state.dynamicServerTime = time;
    },
    setRoles(state, roles) {
      state.roles = roles;
    },
    setStatus(state, status) {
      state.status = status;
    },
    setCategories(state, categories) {
      state.categories = categories;
    },
    setWorkTypes(state, workTypes) {
      state.workTypes = workTypes;
    },
    setUserData(state, value) {
      state.userData = value;
    },
    setDynamicServerTime(state, value) {
      state.dynamicServerTime = value;
    },
  },

  actions: {
    setAuthData({ commit }, data = {}) {
      const decodedToken = jwtDecode(data.access);

      localStorage.setItem('accessToken', data.access);
      localStorage.setItem('refreshToken', data.refresh);

      commit('setLoggedIn', true);
      commit('setExpireToken', new Date(decodedToken.exp * 1000).getTime());
    },
    cleanAuthStorage() {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    },
    async renew({ commit, dispatch }) {
      try {
        const refreshToken = localStorage.getItem('refreshToken');

        if (!refreshToken) {
          dispatch('cleanAuthStorage');
          return;
        }

        const { data } = await api.auth.updateTokens({ refresh: refreshToken });

        dispatch('setAuthData', data.result);
      } catch (error) {
        dispatch('cleanAuthStorage');
        commit('setLoggedIn', false);
        throw error;
      }
    },

    logOut({ commit, dispatch }) {
      dispatch('cleanAuthStorage');
      commit('setLoggedIn', false);
      commit('setRoles', []);
    },

    async loadInfo({ state, commit }) {
      try {
        commit('changeLoading', true);
        if (currentAuthInfoRequest || !state.isLoggedIn) {
          return;
        }
        currentAuthInfoRequest = api.user.info();
        const { data } = await currentAuthInfoRequest;
        const {
          time, roles, status, categories, workTypes,
        } = data.result;
        const userData = removeObjectKeys(data.result, [
          'time',
          'roles',
          'status',
          'categories',
          'workTypes',
        ]);
        commit('setServerTime', time);
        commit('setRoles', roles);
        commit('setStatus', status);
        commit('setCategories', categories);
        commit('setWorkTypes', workTypes);
        commit('setUserData', userData);
        currentAuthInfoRequest = null;
      } catch (error) {
        commit('setLoggedIn', false);
        currentAuthInfoRequest = null;
        throw error;
      } finally {
        commit('changeLoading', false);
      }
    },
    async updateUser({ dispatch }, params) {
      try {
        const result = await api.user.update(params);
        await dispatch('loadInfo');
        return result.data.result;
      } catch (error) {
        return 'Ошибка при обновлении данных';
      }
    },
    async changeEmail({ dispatch }, params) {
      try {
        const result = await api.user.changeEmail(params);
        return result.data.result;
      } catch (error) {
        return 'Ошибка при обновлении данных';
      }
    },
    async changePassword({ dispatch }, params) {
      try {
        const result = await api.user.changePassword(params);
        return result.data.result;
      } catch (error) {
        return 'Ошибка при обновлении данных';
      }
    },
    async restorePassword({ dispatch }, params) {
      return api.user.restorePassword(params);
    },
    async restorePasswordConfirm({ dispatch }, params) {
      return api.user.restorePasswordConfirm(params);
    },
  },

  getters: {
    // просто отдает 1ю роль из списка что не всегда правильно
    getRole(state) {
      const { roles, isLoggedIn } = state;
      if (!isLoggedIn) {
        return 'pub';
      }
      return roles[0] ? roles[0].name : null;
    },
    getRolesString(state) {
      return state.roles.map((role) => role.title).join(', ');
    },
    getFullName(state) {
      const patronymic = state.userData.secondName;
      const { firstName, lastName } = state.userData;
      return [lastName, firstName, patronymic].join(' ');
    },
    getCategoriesString(state) {
      return state.categories.length > 0 ? state.categories.map((category) => category.name).join(', ') : 'Не выбраны';
    },
    getWorkTypesString(state) {
      return state.workTypes.length > 0 ? state.workTypes.map((workType) => workType.name).join(', ') : 'Не выбраны';
    },
    getRoleList(state) {
      return state.roles.map((role) => role.name).filter((n) => n);
    },
    // ищет есть ли нужная роль
    hasRole: (state) => (roleName) => !!state.roles.find((role) => role.name === roleName),
    // отличное решение - отдавать нужную роль если она есть - если нету то первую попавшуюся
    getPriorityRole: (state, getters) => (roleName = '') => {
      let role = getters.getRole;

      if (roleName && getters.hasRole(roleName)) {
        role = roleName;
      }

      return role;
    },
  },
};

export default user;
